/**
 * Deplacement et etat du bonhomme.
 *
 * Deux choix a expliquer:
 *
 * 1. Le terrain et la grille de navigation arrivent par des interfaces
 *    (ITerrain, GrilleNavigation) et pas par les classes concretes. Ce module
 *    reste donc testable seul et ne connait rien du generateur de monde. Une
 *    instance de Terrain ou de GrilleNav est evidemment acceptable en argument.
 *
 * 2. Le calcul de chemin (A*) appartient a shared/sim/navigation.ts. Plutot que
 *    d'importer ce fichier (ce qui ferait dependre toute la simulation du joueur
 *    du module de navigation), on l'injecte UNE fois au demarrage avec
 *    `definirPathfinder(trouverChemin)`. Sans injection, `allerVers` retombe sur
 *    un chemin en ligne droite verifie case par case, ce qui suffit en terrain
 *    degage. C'est explicite, pas un effet de bord cache.
 */
import {
  ARRIVE_EPSILON,
  NAV_CELL,
  MAX_WALK_SLOPE,
  PLAYER_RADIUS,
  RUN_SPEED,
  TURN_SPEED,
  SWIM_SPEED,
  SWIM_SPEED_MAX,
  WALK_SPEED,
  CARTE_X,
  MER_OUEST,
  CARTE_Z,
} from '../constants.ts'
import type { Apparence, Joueur } from '../types/player.ts'
import type { Batiment } from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { BIOMES } from '../data/biomes.ts'
import { clamp, hache2 } from './alea.ts'
import { besoinsNeufs, elanForme, facteurVitesseForme, formeDe, majBesoins, reposerAuSpa } from './besoins.ts'
import { batimentEtageContenant, dansPiscine } from './batiments.ts'

/** Surface publique de GrilleNav dont la simulation du joueur a besoin. */
export interface GrilleNavigation {
  bloquerEmprise(centre: Vec2, l: number, p: number, rot: number): void
  libererEmprise(centre: Vec2, l: number, p: number, rot: number): void
  estLibreMonde(x: number, z: number): boolean
  empriseCouvre(x: number, z: number): boolean
  caseLibreLaPlusProche(x: number, z: number, rayonMax?: number): Vec2 | null
}

export type Pathfinder = (
  grille: GrilleNavigation,
  depart: Vec2,
  arrivee: Vec2,
  budgetNoeuds?: number,
) => Vec2[]

/** Etats verrouilles par une action: la boucle ne doit pas les ecraser. */
const ETATS_VERROUILLES = ['recolte', 'mange', 'dort', 'construit'] as const

/** Cout en energie de chaque etat, de 0 (repos) a 1 (effort). */
const TRAVAIL: Record<Joueur['etat'], number> = {
  immobile: 0,
  marche: 0.1,
  course: 0.3,
  recolte: 1,
  mange: 0,
  dort: 0,
  construit: 0.7,
}

let pathfinderActif: Pathfinder | null = null

/**
 * Branche le vrai A*. A appeler une seule fois au demarrage:
 * `definirPathfinder(trouverChemin)`.
 */
export function definirPathfinder<G extends GrilleNavigation>(
  f: (grille: G, depart: Vec2, arrivee: Vec2, budgetNoeuds?: number) => Vec2[],
): void {
  // La grille passee a l'execution est la vraie GrilleNav; le parametre est
  // contravariant, d'ou la conversion.
  pathfinderActif = f as unknown as Pathfinder
}

/** Chemin de secours: ligne droite echantillonnee, tronquee au premier obstacle. */
export function cheminDirect(grille: GrilleNavigation, depart: Vec2, arrivee: Vec2): Vec2[] {
  const dx = arrivee.x - depart.x
  const dz = arrivee.z - depart.z
  const longueur = Math.hypot(dx, dz)
  if (longueur < 1e-6) return []
  const pas = NAV_CELL / 2
  const n = Math.max(1, Math.ceil(longueur / pas))
  const points: Vec2[] = []
  for (let i = 1; i <= n; i++) {
    const t = i / n
    const x = depart.x + dx * t
    const z = depart.z + dz * t
    if (!grille.estLibreMonde(x, z)) break
    points.push({ x, z })
  }
  return points
}

export function joueurNeuf(nom: string, apparence: Apparence): Joueur {
  let graine = 0x811c9dc5
  for (let i = 0; i < nom.length; i++) {
    graine = Math.imul(graine ^ nom.charCodeAt(i), 16777619) >>> 0
  }
  const id = `j_${(Math.floor(hache2(graine, nom.length, 0x1a2b3c) * 0xffffffff) >>> 0).toString(36)}`
  return {
    id,
    nom,
    pos: { x: 250, z: 250 },
    rot: 0,
    apparence,
    inventaire: {},
    besoins: besoinsNeufs(),
    etat: 'immobile',
    chemin: [],
    direction: null,
    nage: false,
    vehicule: null,
    interieur: null,
    niveau: 0,
    vuA: 0,
  }
}

/**
 * Vrai quand une action tient le bonhomme: il ne se deplace plus tant qu'elle
 * dure. Exporte parce que le SERVEUR pose la meme question — sans elle, il
 * ferait avancer un joueur que son navigateur laisse planter sa hache.
 */
export function verrouille(j: Joueur): boolean {
  return (ETATS_VERROUILLES as readonly string[]).includes(j.etat)
}

/** Plus court ecart angulaire, dans [-PI, PI]. */
function ecartAngle(de: number, vers: number): number {
  let d = (vers - de) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d < -Math.PI) d += Math.PI * 2
  return d
}

/**
 * Vrai si le bonhomme peut occuper (x, z).
 *
 * L'eau n'est PAS un obstacle: on sait nager. La nage se pilote au doigt ou en
 * visant l'eau, jamais par un itineraire maritime — c'est pour ca que la grille,
 * elle, marque l'eau comme bloquee.
 *
 * LE POINT DELICAT, paye par un bogue qui enfermait le nageur en mer. La grille
 * de navigation est PRUDENTE: une case de 2 m est declaree bloquee des qu'UN de
 * ses quatre coins l'est. Tout le long du littoral, la derniere bande de sable
 * touche l'eau par un coin, donc toute la cote du monde etait bloquee sur un a
 * deux metres. Le nageur pouvait entrer dans la mer et plus jamais en sortir: il
 * venait buter contre cette bande seche mais interdite, sur les quatre rivages.
 *
 * D'ou la regle: la grille sert a PLANIFIER (l'A* trace prudemment), le terrain
 * reel sert a SE DEPLACER (le bonhomme est un point, pas une case). Quand la
 * grille refuse, on redemande donc au terrain lui-meme. Ce qui reste
 * infranchissable l'est vraiment: le bord du monde, une pente de falaise, et
 * l'emprise d'une construction, qui n'est pas une approximation.
 */
export function positionPossible(
  grille: GrilleNavigation,
  terrain: ITerrain,
  x: number,
  z: number,
  depuis?: Vec2,
): boolean {
  if (x < -MER_OUEST + PLAYER_RADIUS || x > CARTE_X - PLAYER_RADIUS) return false
  if (z < PLAYER_RADIUS || z > CARTE_Z - PLAYER_RADIUS) return false
  // A l'ouest de x = 0 il n'y a que la mer ouverte (MER_OUEST): on y nage.
  if (x < PLAYER_RADIUS) return terrain.estEau(x, z)
  if (grille.empriseCouvre(x, z)) return false
  if (terrain.estEau(x, z)) return true
  if (grille.estLibreMonde(x, z)) return true
  if (terrain.pente(x, z) <= MAX_WALK_SLOPE) return true
  return monteeAcceptable(terrain, x, z, depuis)
}

/**
 * ON DESCEND TOUJOURS, ON MONTE UN PEU.
 *
 * LE BOGUE QUE CECI REPARE. `pente(x, z)` est la RAIDEUR DU LIEU — la norme du
 * gradient, c'est-a-dire la pente dans la direction la plus raide. Un flanc de
 * montagne est raide partout: en interrogeant le seul lieu d'arrivee, on
 * refusait donc TOUTES les directions, y compris celles qui descendent et
 * celles qui longent la courbe de niveau. Quelqu'un qui se retrouvait la-haut
 * ne pouvait plus bouger d'un metre, dans aucun sens — et le filet
 * anti-blocage ne le sauvait pas, puisque l'endroit ou il se tenait, lui,
 * etait parfaitement legal. Amelia a passe une soiree plantee sur un sommet.
 *
 * La raideur d'un lieu ne dit rien de ce qu'on est en train d'y faire. Ce qui
 * compte, c'est la MONTEE DU PAS: la difference de hauteur divisee par la
 * distance parcourue. Descendre donne un rapport negatif — toujours accepte,
 * c'est la demande du proprietaire, « on doit toujours pouvoir redescendre ».
 * Longer donne zero. Grimper est plafonne au meme `MAX_WALK_SLOPE` qu'avant,
 * ce qui laisse de quoi ressortir d'un creux sans laisser escalader une
 * falaise: « un peu, mais pas n'importe quoi ».
 *
 * SANS ORIGINE, ON REFUSE, et c'est voulu: `positionPossible` sert aussi a
 * demander « peut-on se tenir la ? » sans qu'aucun pas soit en cours (le filet
 * anti-blocage, la premiere position que le serveur accorde). Cette
 * question-la garde exactement sa reponse d'avant.
 *
 * Cette regle est un SUR-ENSEMBLE de l'ancienne: tout ce qui passait passe
 * encore. Personne ne peut donc se retrouver coince par elle a un endroit qui
 * ne l'aurait pas deja coince.
 */
function monteeAcceptable(terrain: ITerrain, x: number, z: number, depuis?: Vec2): boolean {
  if (!depuis) return false
  const dx = x - depuis.x
  const dz = z - depuis.z
  const course = Math.hypot(dx, dz)
  // Un pas nul n'est pas une montee: on a le droit de RESTER la ou l'on est,
  // sinon le filet anti-blocage arracherait a son flanc celui qui vient d'y
  // descendre en toute legalite — deux regles qui se renvoient la balle.
  if (course < 1e-6) return true
  const montee = terrain.hauteur(x, z) - terrain.hauteur(depuis.x, depuis.z)
  return montee / course <= MAX_WALK_SLOPE
}

/**
 * Pourquoi une position est refusee. Sert au journal: sans cette raison, un
 * « debloque de (5.8, 230.2) » ne dit pas s'il s'agissait d'une falaise, d'une
 * piscine ou du bord du monde, et il fallait reconstruire le terrain dans un
 * script pour le savoir.
 */
export function raisonPositionImpossible(
  grille: GrilleNavigation,
  terrain: ITerrain,
  x: number,
  z: number,
): string | null {
  if (x < -MER_OUEST + PLAYER_RADIUS || x > CARTE_X - PLAYER_RADIUS) return 'bord de la carte en x'
  if (z < PLAYER_RADIUS || z > CARTE_Z - PLAYER_RADIUS) return 'bord de la carte en z'
  if (x < PLAYER_RADIUS) return terrain.estEau(x, z) ? null : 'hors de la mer ouverte'
  if (grille.empriseCouvre(x, z)) return 'emprise de construction'
  if (terrain.estEau(x, z)) return null
  if (grille.estLibreMonde(x, z)) return null
  const pente = terrain.pente(x, z)
  if (pente > MAX_WALK_SLOPE) return `pente ${pente.toFixed(2)} > ${MAX_WALK_SLOPE}`
  return null
}

/**
 * Vrai si le bonhomme peut occuper (x, z) en venant de la direction (ux, uz):
 * on teste le centre et un point sonde devant lui, sinon on rentre dans les
 * murs de la moitie du rayon.
 */
function occupable(
  grille: GrilleNavigation,
  terrain: ITerrain,
  x: number,
  z: number,
  ux: number,
  uz: number,
  depuis: Vec2,
): boolean {
  if (!positionPossible(grille, terrain, x, z, depuis)) return false
  return positionPossible(grille, terrain, x + ux * PLAYER_RADIUS, z + uz * PLAYER_RADIUS, depuis)
}

/** Ce que le vehicule change au deplacement du joueur. */
export interface OptionsDeplacement {
  /** Multiplicateur de vitesse: 3 pour la voiture, 2 pour la barque. */
  multiplicateur?: number
  /**
   * Vrai quand le joueur est a bord. A bord on ne nage pas: sans ca le plafond
   * de la nage ramenait la barque a 1,9 m/s et le bonhomme nageait assis dedans.
   */
  aBord?: boolean
}

export function majJoueur(
  j: Joueur,
  dt: number,
  terrain: ITerrain,
  grille: GrilleNavigation,
  dtMsJeu: number,
  options?: OptionsDeplacement,
): void {
  if (!verrouille(j)) {
    const intention = intentionDe(j)
    if (intention) appliquerIntention(j, intention, dt, terrain, grille, formeDe(j.besoins), options)
  }
  majNageEtBesoins(j, terrain, dtMsJeu, options)
}

/**
 * La part de `majJoueur` qui ne DEPLACE pas: nage et besoins.
 *
 * Separee parce que le multijoueur fait rejouer le deplacement seul (voir
 * shared/sim/entree.ts): le serveur ne tient ni la faim ni l'energie, et le
 * client doit pouvoir bouger d'un cote et se nourrir de l'autre.
 */
export function majNageEtBesoins(
  j: Joueur,
  terrain: ITerrain,
  dtMsJeu: number,
  options?: OptionsDeplacement,
  /**
   * Vrai quand le bonhomme trempe dans le spa d'une terrasse (voir `dansSpa`).
   *
   * Volontairement un argument, et pas une lecture des batiments faite ici: les
   * besoins ne sont PAS en autorite serveur (comme les inventaires), et ce
   * module n'a pas a decider tout seul quand un bain commence. Le spa ne rend
   * pas `nage` vrai — la nage est l'etat de la mer et de la piscine, elle
   * enfonce le bonhomme et lui donne la brasse, ce qui serait absurde dans un
   * bain a remous.
   */
  spa = false,
): void {
  // Nager coute de l'energie, et on ne nage pas en dormant ni a bord.
  j.nage = !options?.aBord && j.etat !== 'dort' && terrain.estEau(j.pos.x, j.pos.z)
  const effort = j.nage && j.etat !== 'immobile' ? Math.max(TRAVAIL[j.etat], 0.45) : TRAVAIL[j.etat]
  j.besoins = majBesoins(j.besoins, dtMsJeu, effort)
  if (spa) j.besoins = reposerAuSpa(j.besoins, dtMsJeu)
}

/**
 * CE QUE LE JOUEUR DEMANDE pendant un tick: une direction et la vitesse qu'il
 * vise, avant tout ce qui la module (biome, faim, nage, vehicule).
 *
 * C'EST LA PIECE QUI VOYAGE SUR LE RESEAU. Le client n'envoie plus sa position
 * mais ceci, et le serveur le rejoue: pour que les deux tombent sur le meme
 * resultat, il faut que la meme fonction fasse le meme calcul des deux cotes a
 * partir des memes nombres. D'ou la coupure en deux — « ce que je veux », qui
 * se transmet, et « ce que ca donne », qui se recalcule.
 */
export interface Intention {
  /** Direction visee, NON normalisee: c'est le vecteur brut vers la cible. */
  dx: number
  dz: number
  /** Vitesse voulue en m/s, avant les modificateurs. */
  vitesse: number
}

/**
 * L'intention du tick, deduite du chemin ou de la manette.
 *
 * ELLE MODIFIE `j`: elle consomme les points de chemin atteints et pose
 * `immobile` quand il n'y a rien a faire, exactement comme le faisait
 * `deplacer`. C'est le generateur d'intention, il vit donc du cote du joueur
 * qui la produit — le serveur, lui, ne l'appelle jamais: il recoit le resultat.
 */
/**
 * Rayon dans lequel un point de passage DEPASSE compte comme atteint. Un peu
 * plus que le pas d'une voiture lancee (RUN_SPEED x 3 x TICK_DT = 1,05 m).
 */
export const PORTEE_DEPASSEMENT = 1.5

/**
 * Le point `p` est-il deja DERRIERE nous?
 *
 * Une voiture fait 1,05 m par tick, et un point de passage ne comptait comme
 * atteint qu'a 0,35 m: elle sautait par-dessus, se retournait pour revenir le
 * chercher, sautait de nouveau — la navette dans les virages serres. Un point
 * proche qui est dans notre dos (derriere le nez, `rot`) ou deja passe dans le
 * sens du troncon suivant ne merite pas un demi-tour: on vise le suivant. Le
 * sens du troncon seul ne suffit pas: a un angle droit, la voiture qui file
 * tout droit au-dela du coin lui est PERPENDICULAIRE. Borne a `PORTEE_DEPASSEMENT` pour ne
 * pas couper un detour qui contourne un obstacle. Le dernier point n'a pas de
 * suivant: on y arrive a la vitesse de marche, dont le pas tient dans la zone.
 *
 * Code du CLIENT seulement: le serveur ne voit pas le chemin, il recoit une
 * direction et une vitesse (`entreeDeTick`). Rien ne change sur le fil.
 */
function depasse(pos: Vec2, rot: number, p: Vec2, suivant: Vec2 | undefined, d: number): boolean {
  if (!suivant || d > PORTEE_DEPASSEMENT) return false
  if ((pos.x - p.x) * (suivant.x - p.x) + (pos.z - p.z) * (suivant.z - p.z) > 0) return true
  // Dans le dos: meme convention que `rot = atan2(ux, uz)`.
  return (p.x - pos.x) * Math.sin(rot) + (p.z - pos.z) * Math.cos(rot) < 0
}

export function intentionDe(j: Joueur): Intention | null {
  let dirx = 0
  let dirz = 0
  let vitesse = 0

  if (j.chemin.length > 0) {
    // Tap-to-move: on consomme les points atteints.
    while (j.chemin.length > 0) {
      const p = j.chemin[0] as Vec2
      const d = Math.hypot(p.x - j.pos.x, p.z - j.pos.z)
      if (d <= ARRIVE_EPSILON || depasse(j.pos, j.rot, p, j.chemin[1], d)) j.chemin.shift()
      else break
    }
    const p = j.chemin[0]
    if (p === undefined) {
      j.etat = 'immobile'
      return null
    }
    dirx = p.x - j.pos.x
    dirz = p.z - j.pos.z
    const fin = j.chemin[j.chemin.length - 1] as Vec2
    const restant = Math.hypot(fin.x - j.pos.x, fin.z - j.pos.z)
    // On court quand il reste du chemin, on marche a l'approche.
    vitesse = restant > 6 ? RUN_SPEED : WALK_SPEED
  } else if (j.direction !== null) {
    dirx = j.direction.x
    dirz = j.direction.z
    const manette = clamp(Math.hypot(dirx, dirz), 0, 1)
    if (manette < 0.05) {
      j.etat = 'immobile'
      return null
    }
    // Manette a fond: on court. Poussee partielle: on marche au prorata.
    vitesse = manette >= 0.95 ? RUN_SPEED : WALK_SPEED * (manette / 0.95)
  } else {
    j.etat = 'immobile'
    return null
  }

  if (Math.hypot(dirx, dirz) < 1e-6) {
    j.etat = 'immobile'
    return null
  }
  return { dx: dirx, dz: dirz, vitesse }
}

/**
 * LE PLAFOND DE LA NAGE, selon la forme du nageur.
 *
 * On nage a SWIM_SPEED quand on est a plat, et jusqu'a SWIM_SPEED_MAX quand on
 * est repose et rassasie. Rien ne le distingue d'une regle de jeu ordinaire
 * SAUF ceci: elle est calculee ICI, dans le pas partage, a partir du seul
 * nombre qui voyage (`Entree.f`). Le navigateur et le serveur tombent donc sur
 * le meme plafond au bit pres, sinon le nageur tressauterait a chaque reponse
 * du serveur. Un client qui mentirait sur sa forme ne gagnerait que
 * SWIM_SPEED_MAX, jamais un metre par seconde de plus.
 */
export function vitesseNage(forme: number): number {
  return SWIM_SPEED + (SWIM_SPEED_MAX - SWIM_SPEED) * elanForme(forme)
}

/**
 * Applique une intention: c'est ici, et nulle part ailleurs, que le bonhomme
 * bouge.
 *
 * `forme` arrive en ARGUMENT au lieu d'etre lue dans `j.besoins`: le serveur ne
 * tient pas la faim des joueurs, il la recoit avec l'intention, bornee a [0, 1]
 * (voir shared/sim/entree.ts). Un client ne peut donc mentir que dans un sens —
 * se pretendre en pleine forme, ce qui lui evite le ralentissement de la faim
 * et lui donne le plafond de nage du repose. Rien au-dela.
 */
export function appliquerIntention(
  j: Joueur,
  intention: Intention,
  dt: number,
  terrain: ITerrain,
  grille: GrilleNavigation,
  forme: number,
  options?: OptionsDeplacement,
): void {
  const norme = Math.hypot(intention.dx, intention.dz)
  if (norme < 1e-6) {
    j.etat = 'immobile'
    return
  }
  const ux = intention.dx / norme
  const uz = intention.dz / norme
  let vitesse = intention.vitesse

  /*
   * Le sable et la neige ralentissent, la faim et la fatigue aussi.
   *
   * C'est le LIEU qui repond en premier, et le biome seulement s'il se tait:
   * les onze regions de l'ile 2 n'ont que huit biomes entre elles, et la neige
   * du Paradis Polaire n'a pas a ralentir comme le sable de l'Anse.
   *
   * ON NE PASSE PAS PAR `echantillon` POUR AUTANT, et c'est mesure: il calcule
   * la PENTE, donc quatre lectures de hauteur de plus — a chaque tick, pour
   * chaque joueur, des deux cotes du fil. `vitesseDuLieu` est une question
   * simple qui merite une reponse simple.
   */
  const biome = terrain.biome(j.pos.x, j.pos.z)
  // Dans l'eau on plafonne a la vitesse de nage: pas de sprint aquatique. Mais
  // a bord d'une barque on navigue, on ne nage pas. Le plafond depend de la
  // forme: repose, on file; a plat, on retombe sur SWIM_SPEED, exactement comme
  // avant. L'ordre compte — le plafond D'ABORD, la penalite ENSUITE, sinon un
  // nageur affame irait plus vite qu'un nageur rassasie plafonne.
  if (!options?.aBord && terrain.estEau(j.pos.x, j.pos.z)) {
    vitesse = Math.min(vitesse, vitesseNage(forme))
  }
  const duLieu = terrain.vitesseDuLieu?.(j.pos.x, j.pos.z) ?? BIOMES[biome].vitesse
  vitesse *= facteurVitesseForme(forme) * duLieu * (options?.multiplicateur ?? 1)

  const pas = vitesse * dt
  const avantX = j.pos.x
  const avantZ = j.pos.z
  const cx = j.pos.x + ux * pas
  const cz = j.pos.z + uz * pas

  // Glissement: le mouvement complet, sinon un seul axe, plutot qu'un blocage net.
  // `depart` est l'origine du pas: c'est elle qui dit si l'on monte ou si l'on
  // descend (voir `monteeAcceptable`). Les deux cotes du fil la calculent de la
  // meme facon, puisque c'est la meme fonction qui fait le pas.
  const depart = { x: avantX, z: avantZ }
  if (occupable(grille, terrain, cx, cz, ux, uz, depart)) {
    j.pos.x = cx
    j.pos.z = cz
  } else if (Math.abs(ux) > 1e-6 && occupable(grille, terrain, cx, j.pos.z, Math.sign(ux), 0, depart)) {
    j.pos.x = cx
  } else if (Math.abs(uz) > 1e-6 && occupable(grille, terrain, j.pos.x, cz, 0, Math.sign(uz), depart)) {
    j.pos.z = cz
  }

  const parcouru = Math.hypot(j.pos.x - avantX, j.pos.z - avantZ)

  // Rotation lissee vers la direction visee, meme si on est bloque.
  const cible = Math.atan2(ux, uz)
  const ecart = ecartAngle(j.rot, cible)
  const max = TURN_SPEED * dt
  j.rot += clamp(ecart, -max, max)

  if (parcouru < 1e-5) {
    j.etat = 'immobile'
    // Bloque contre un obstacle: inutile de garder un chemin infaisable.
    if (j.chemin.length > 0) j.chemin.length = 0
    return
  }
  // Le nageur reste toujours en `marche`: SWIM_SPEED_MAX (2,8) est sous ce
  // seuil (5,5), et c'est voulu. Le passer en `course` lui coincerait TRAVAIL a
  // 0,3 et lui viderait l'energie qui lui donne justement son elan. La depense
  // de la nage est deja prise en compte ailleurs, une fois: `majNageEtBesoins`
  // impose un effort d'au moins 0,45 des qu'on nage en bougeant.
  j.etat = vitesse >= (WALK_SPEED + RUN_SPEED) / 2 ? 'course' : 'marche'
}

/**
 * Calcule un chemin vers `cible` et le pose dans `j.chemin`.
 * Faux si aucun chemin: l'interface peut alors afficher un refus.
 */
export function allerVers(j: Joueur, cible: Vec2, grille: GrilleNavigation): boolean {
  let arrivee = cible
  if (!grille.estLibreMonde(arrivee.x, arrivee.z)) {
    const proche = grille.caseLibreLaPlusProche(arrivee.x, arrivee.z)
    if (proche === null) return false
    arrivee = proche
  }
  const chemin = pathfinderActif
    ? pathfinderActif(grille, j.pos, arrivee)
    : cheminDirect(grille, j.pos, arrivee)
  if (chemin.length === 0) return false
  j.chemin = chemin
  j.direction = null
  return true
}

export function arreter(j: Joueur): void {
  j.chemin.length = 0
  j.direction = null
  if (!verrouille(j)) j.etat = 'immobile'
}

/**
 * Ou remettre un bonhomme qui s'est retrouve dans une case impossible, ou
 * `null` s'il n'y a rien a corriger.
 *
 * DEUX REGLES POUR LA MEME QUESTION FINISSENT TOUJOURS PAR DIVERGER — c'est
 * ecrit noir sur blanc dans les pieges du projet, et ca a deja coute 66
 * « Tu etais coince » en quatorze minutes. Le filet du navigateur et celui du
 * serveur DOIVENT donc etre la meme fonction: si le client se degageait tout
 * seul, le serveur, qui ne saurait pas pourquoi, le renverrait aussitot dans le
 * trou, et le bonhomme ferait la navette entre les deux tant que le joueur
 * n'aurait pas ferme l'onglet.
 *
 * La piscine est une exception assumee: on y nage, ses murs sont geres
 * ailleurs (`resoudreMurs`), et la grille la declare bloquee.
 *
 * LA TERRASSE EN EST UNE SECONDE, et de la meme famille. La grille et le
 * terrain ne connaissent que la COLONNE au sol: a l'etage, ils repondent pour
 * un endroit ou le joueur ne se trouve pas. Tant qu'il est sur l'emprise de son
 * batiment, l'acrotere fait deja tout le travail et le filet n'a rien a dire —
 * s'il parlait, il jetterait le bonhomme du toit vers la case libre la plus
 * proche, en bas.
 */
export function debloquer(
  grille: GrilleNavigation,
  terrain: ITerrain,
  batiments: readonly Batiment[],
  x: number,
  z: number,
  niveau = 0,
  depuis?: Vec2,
): Vec2 | null {
  /*
   * `depuis` est l'origine du pas qui vient d'etre joue, et il est ici pour une
   * raison precise: depuis que l'on a le droit de DESCENDRE n'importe quel
   * flanc (voir `monteeAcceptable`), le filet doit accepter les endroits ou ce
   * droit vient de nous mener. Sans lui, le pas autoriserait la descente et le
   * filet la defaudrait aussitot, un tick sur deux, avec « Tu etais coince » a
   * chaque fois — deux regles pour la meme question, le piege que ce projet
   * s'interdit.
   */
  if (positionPossible(grille, terrain, x, z, depuis)) return null
  if (dansPiscine(batiments, x, z)) return null
  if (niveau > 0 && batimentEtageContenant(batiments, x, z, PLAYER_RADIUS) !== null) return null
  return grille.caseLibreLaPlusProche(x, z, 30)
}
